import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from 'src/app/services/user.service';
import { AccountsService } from 'src/app/services/accounts.service';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { accountsFeature, AccountsState } from 'src/app/store/accounts.era';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { PhotoComponent } from 'src/app/components/photo';
import { JoiAccount } from 'globals/store/accounts.data';

@Component({
    selector: 'change-event-account-dialog',
    template: `
      <div class="joi-dialog">
          <div mat-dialog-title>
             Change Event Account
          </div>
          <div mat-dialog-content>
              <div class="dialog_content">
                  <div class="para">
                      {{account_usage_message}}
                  </div>
                  <div>
                      <div class="para">
                          Change the account for <strong>{{event_name}}</strong>
                      </div>
                  </div>
                  <div class="label">
                      Account
                  </div>
                  <div *ngIf="accounts.length == 1 || !is_admin_on_source" class="para flex">
                      <photo [data]="use_account"></photo>
                      <div class="name">{{use_account.title}}</div>
                  </div>
                  <div *ngIf="accounts.length > 1 && is_admin_on_source" class="para">
                      <div [matMenuTriggerFor]="useAccount" class="dropdown_label">
                          <photo [data]="use_account"></photo>
                          <div class="name">{{use_account.title}}</div>
                          <icon class="drop" matIcon="arrow_drop_down"></icon>
                      </div>
                      <mat-menu #useAccount="matMenu" xPosition="before">
                          <button *ngFor="let acc of accounts"
                              mat-menu-item (click)="useThisAccount(acc)"
                              [disabled]="use_account.id === acc.id || acc.is_readonly || acc.is_invited">{{acc.title || acc.id}}</button>
                      </mat-menu>
                  </div>
                  <div class="will_start_trial_message" *ngIf="will_start_trial">Note: Creating an event will start your 30 day free trial.</div>
              </div>
          </div>
          <div mat-dialog-actions class="lg">
              <button mat-button (click)="dialogRef.close()">Cancel</button>
              <button mat-button color="primary" [disabled]="!next_account || !can_create" (click)="updateEvent()">Update event account</button>
              <button mat-button color="primary" *ngIf="!can_create && next_account" (click)="goToSubscriptions()">Get more events</button>
          </div>
      </div>
  `,
    styles: [`
  .joi-dialog .para { margin-bottom: 15px; }
  .joi-dialog .flex { display: flex; }
  .joi-dialog .label { font-size:11px; font-weight:bold; color:#999; text-transform:uppercase; padding-bottom: 5px; }
  .joi-dialog .name { line-height: 21px; flex-grow:1;}
  .joi-dialog .dropdown_label, .drop { display: flex; cursor: pointer; }
  .joi-dialog .will_start_trial_message { position: relative; top: 15px; color: #999; font-style: italic; }
  .joi-dialog .date_selection { padding-top: 10px; }
  .joi-dialog .date { font-weight: bold; padding: 5px 0 20px;}
  .joi-dialog .date_picker { top:0; }
  `],
    standalone: true,
    imports: [CommonModule, MatMenuModule, IconComponent, PhotoComponent, ],
})
export class ChangeEventAccountDialogComponent {

  event_name;
  accounts;
  use_account;
  next_account: JoiAccount;
  can_create = false;
  is_admin_on_source;
  will_start_trial = false;
  account_usage_message = '';
  formatted_existing_date;
  formatted_date;

  constructor( public dialogRef: MatDialogRef<ChangeEventAccountDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
      public accountService:AccountsService,
      public router:Router,
      public userService:UserService,
      public store: Store
    ) {
      // console.log('data ', data);
      this.event_name = data.event.title;

      this.store.pipe(select(accountsFeature.selectAccountsState), take(1)).subscribe((accounts: AccountsState) => {


        const accountId = this.data.event.accountId
        this.accounts = Object.values(accounts.entities).filter(a => a.id !== accountId)
        const otherActiveAccounts = this.accounts.filter(this.isActive)
        const otherAccountWithOpenEvents = otherActiveAccounts.find(a => a.docket.event_limit > (a.docket.tally || 0))
        if (otherAccountWithOpenEvents) {
          this.useThisAccount(otherAccountWithOpenEvents, true)
        } else if (otherActiveAccounts.length) {
          this.useThisAccount(otherActiveAccounts[0])
        } else if (this.accounts.length) {
          this.useThisAccount(this.accounts[0])
        } else {
          console.log("No other accounts to use!!!  This dialog window should not be open")
          this.useThisAccount(accounts.entities[accountId])
        }
        this.is_admin_on_source = accounts.entities[accountId].admin.findIndex(id=>id===userService.uid) > -1;
      })
    }

  isActive(account) {
    if (!account || !account.docket) {
      return console.log("Docket or account missing: ", account)
    }
    return ['fresh', 'custom', 'incomplete', 'active', 'trial', 'beta'].includes(account.docket.status)
  }

  useThisAccount(account,from_init?) {
    // todo: make this account their default
    this.use_account = account;

    if (!account) return;
    this.next_account = account;
    // docket parse
    this.will_start_trial = false;
    const active_events = account.docket.tally || 0;
    const event_limit = account.docket.limit;
    this.account_usage_message = 'No events available.  ';
    const is_active = this.isActive(account);
    this.can_create = false
    if (account.is_suspended) {
      this.account_usage_message += "This account is suspended.";
    } else if (!is_active) {
      this.account_usage_message += "This account is not active.";
    } else if (event_limit === null && is_active) {
      this.can_create = true;
      this.account_usage_message = '';
      if (account.docket.status === 'fresh') {
        this.will_start_trial = true;
      }
    } else if (event_limit <= active_events) {
      this.account_usage_message += "This account has used up the " + event_limit + " available event" + (event_limit === 1 ? '' : 's') + ".";
    } else {
      this.can_create = true;
      this.account_usage_message = 'This account has used '
        + active_events + ' of it\'s '
        + event_limit + ' active events';
    }
  }

  goToSubscriptions() {
    this.router.navigate(['/account/'+ this.use_account.id]);
    this.dialogRef.close();
  }

  updateEvent(){
      this.dialogRef.close(this.next_account)
  }
}


